




















































































import { computed, nextTick, ref, watch, defineComponent } from '@vue/composition-api';
import { BaseParagraph, ParagraphSize } from '../base-paragraph';
import { BasePopper } from '../base-popper';
import { BaseH5 } from '../base-headings';
import SelectInput from './SelectInput.vue';
import SelectOption from './SelectOption.vue';

export default defineComponent({
  name: 'BaseSelectV2',
  components: {
    BaseParagraph,
    BasePopper,
    BaseH5,
    SelectInput,
    SelectOption,
  },
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    label: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    searchable: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
    helpMessage: { type: String, default: '' },
    success: { type: Boolean, default: false },
    optionLabel: { type: String, default: '' },
    optionKey: { type: String, default: '' },
    optionGroupKey: { type: String, default: '' },
    optionGroupLabel: { type: String, default: '' },
    // eslint-disable-next-line vue/require-prop-types
    options: { default: () => [] },
    emptyStateText: { type: String, default: '' },
    // eslint-disable-next-line vue/require-prop-types
    value: { default: null },
    stayOpenOnSelect: { type: Boolean, default: false },
    appendToBody: { type: Boolean, default: false },
    fixedLabel: { type: Boolean, default: false },
    dataExternal: { type: String, default: '' },
  },
  setup(props, { emit }) {
    const search = ref('');
    const isOpen = ref(false);
    const focusedIndex = ref(-1);

    watch(isOpen, () => {
      if (!isOpen.value) {
        focusedIndex.value = 0;
      }
    });

    const getDataExternal = option => {
      if (typeof formattedKey(option) === 'string' || typeof formattedKey(option) === 'number') {
        return `${props.dataExternal}:${formattedKey(option)}-btn`;
      }
      return `${props.dataExternal}:${formattedLabel(option)}-btn`;
    };

    const hasGroups = computed(() => !!props.options[0]?.[props.optionGroupKey]);
    const flattenedOptions = computed(() =>
      hasGroups.value
        ? props.options.map(group => group[props.optionGroupKey]).flat()
        : props.options
    );
    const flattenedFilteredOptions = computed(() =>
      hasGroups.value
        ? filteredOptions.value.map(group => group[props.optionGroupKey]).flat()
        : filteredOptions.value
    );
    const selectedOption = computed(() => {
      if (hasGroups.value) {
        return flattenedOptions.value.find(option => option[props.optionKey] === props.value);
      }
      if (props.optionKey) {
        return props.options.find(option => option[props.optionKey] === props.value);
      }

      return props.value;
    });
    const focusedOption = computed(() =>
      hasGroups.value
        ? flattenedFilteredOptions.value[focusedIndex.value]
        : filteredOptions.value[focusedIndex.value]
    );
    const focusedLabel = computed(() => formattedLabel(focusedOption.value));
    const inputPlaceholder = computed(
      () => formattedLabel(selectedOption.value) || props.placeholder
    );
    const inputValue = computed(() =>
      isOpen.value && props.searchable ? search.value : formattedLabel(selectedOption.value)
    );

    const filteredOptions = computed(() => {
      if (!props.options) {
        return [];
      }
      if (search.value && hasGroups.value) {
        const searchRegex = new RegExp(`(\\W|^)${search.value}`, 'i');
        return props.options.map(group => ({
          ...group,
          [props.optionGroupKey]: group[props.optionGroupKey].filter(
            option => formattedLabel(option).search(searchRegex) !== -1
          ),
        }));
      }
      if (search.value) {
        const searchRegex = new RegExp(`(\\W|^)${search.value}`, 'i');
        return props.options.filter(option => formattedLabel(option).search(searchRegex) !== -1);
      }
      return props.options;
    });

    const select = option => {
      emit('update', formattedKey(option));
      search.value = '';
      focusedIndex.value = flattenedFilteredOptions.value.findIndex(item => item === option);
      if (!props.stayOpenOnSelect) {
        close();
      }
    };

    const toggle = () => {
      if (isOpen.value) {
        close();
        return;
      }
      open();
    };

    const handleSearch = (searchString: string | number) => {
      search.value = `${searchString}`;
      focusedIndex.value = 0;
    };

    const handleArrowDown = () => {
      if (!isOpen.value) {
        open();
        return;
      }
      if (focusedIndex.value === flattenedFilteredOptions.value.length - 1) return;
      focusedIndex.value++;
      scrollFocusIntoView(focusedOption.value);
    };

    const handleArrowUp = () => {
      if (!isOpen.value) {
        open();
        return;
      }
      if (focusedIndex.value === 0) return;
      if (focusedIndex.value !== null) focusedIndex.value--;
      scrollFocusIntoView(focusedOption.value);
    };

    const scrollFocusIntoView = option => {
      const activeElement: HTMLElement | null = document.getElementById(
        props.options.length && getDataExternal(option)
      );
      if (activeElement) {
        nextTick(() => {
          activeElement.scrollIntoView({ block: 'nearest' });
        });
      }
    };
    const enter = (event: KeyboardEvent) => {
      if (!isOpen.value) {
        open();
        return;
      }
      event.preventDefault();
      select(focusedOption.value);
    };

    const space = (event: KeyboardEvent) => {
      if (!props.searchable) {
        enter(event);
      }
    };

    const open = () => {
      isOpen.value = true;
      focusedIndex.value = selectedOption.value
        ? flattenedOptions.value.findIndex(option => formattedKey(option) === props.value)
        : -1;
      scrollFocusIntoView(selectedOption.value);
    };

    const close = () => {
      search.value = '';
      isOpen.value = false;
    };

    const handleBlur = () => {
      emit('blur');
      close();
    };
    const handleFocus = () => {
      emit('focus');
    };

    const formattedLabel = (option): string =>
      props.optionLabel ? option?.[props.optionLabel] : option;
    const formattedKey = option => (props.optionKey ? option?.[props.optionKey] : option);
    const selected = option => props.value == formattedKey(option);
    const focused = (option): boolean => option === focusedOption.value;

    return {
      formattedLabel,
      formattedKey,
      selected,
      focused,
      enter,
      handleSearch,
      toggle,
      handleArrowDown,
      handleArrowUp,
      select,
      focusedLabel,
      inputValue,
      inputPlaceholder,
      filteredOptions,
      ParagraphSize,
      isOpen,
      close,
      hasGroups,
      space,
      getDataExternal,
      handleBlur,
      handleFocus,
    };
  },
});
